import type { calendar as en } from "../en/calendar";
import type { Messages } from "../types";

export const calendar: Messages<typeof en> = {
  types: {
    public_holiday: "عطلة رسمية",
    national_day: "يوم وطني",
    religious: "مناسبة دينية",
    observance: "مناسبة",
    international_day: "يوم دولي",
    crisis: "أزمة",
    global_event: "حدث عالمي",
  },
  moods: { celebratory: "سعيدة", neutral: "محايدة", solemn: "حزينة", sensitive: "حساسة" },
  moodHints: {
    celebratory: "احتفالات: التهاني والعروض مناسبة",
    neutral: "أيام توعوية",
    solemn: "أيام حزن أو ذكرى: بدون عروض",
    sensitive: "تحتاج عناية",
  },
  sources: { nager: "العطل الرسمية", curated: "قائمة ملفك المعتمدة", hijri: "أم القرى (محسوب)", reliefweb: "التنبيهات الإنسانية (الأمم المتحدة)", gdelt: "إشارات الأخبار", brave: "البحث على الويب" },
  tones: { celebratory: "احتفالية", warm: "دافئة", respectful: "وقورة", informative: "توعوية", silent: "التزم الصمت" },
  severity: { low: "منخفضة", medium: "متوسطة", high: "عالية", critical: "حرجة" },
  worldwide: "حول العالم",
  approximate: "±يوم",
  approximateHint: "يعتمد على رؤية الهلال، وقد يتغير يوماً",
  needsReview: "بحاجة لمراجعة",
  dismissed: "مخفية",
  event: {
    dates: "التاريخ",
    allDay: "طوال اليوم",
    type: "النوع",
    mood: "الطابع",
    sources: "المصادر",
    openSource: "فتح المصدر",
    confidence: "الثقة: {{value}}",
    severity: "الخطورة",
    guidance: "إرشادات النشر",
    tone: "النبرة",
    do: "افعل",
    dont: "تجنّب",
    ideas: "أفكار منشورات",
    hashtags: "وسوم",
    createPost: "أنشئ منشوراً لهذا اليوم",
    dismiss: "إخفاء عن فريقي",
    restore: "إظهار مجدداً",
    approve: "تأكيد الأزمة",
    reject: "غير ذات صلة",
    reviewHint: "رُصدت من إشارات الأخبار. أكّدها لتنبيه فريقك وتمييز المنشورات المجدولة.",
    dismissedToast: "أُخفيت من تقويمك",
    restoredToast: "عادت إلى تقويمك",
    approvedToast: "تم تأكيد الأزمة وتنبيه فريقك",
    rejectedToast: "عُلّمت كغير ذات صلة",
    more: "+{{n}} مناسبات",
    eventsOn: "مناسبات يوم {{date}}",
  },
  overlay: { allDay: "طوال اليوم" },
  subscribe: {
    button: "اشتراكات التقويم",
    title: "اشتراكات التقويم",
    description: "اختر الدول وأنواع الأيام والطابع الذي يظهر في تقويمك. تتحدّث المعاينة قبل الحفظ.",
    scope: "ينطبق على",
    orgDefault: "المؤسسة كاملة",
    inherits: "هذا الحساب يتبع إعدادات المؤسسة. الحفظ ينشئ إعداداً خاصاً به.",
    override: "إعداد خاص بهذا الحساب.",
    resetToOrg: "اتبع إعدادات المؤسسة",
    resetDone: "عاد هذا الحساب لاتباع إعدادات المؤسسة",
    countries: "الدول",
    searchCountries: "ابحث عن دولة",
    selected: "{{n}} مختارة",
    noMatch: "لا توجد دولة تطابق «\u2068{{q}}\u2069»",
    moodsFor: "ما يظهر لـ{{country}}",
    categories: "أنواع الأيام",
    defaultMoods: "الطابع",
    crisisAlerts: "تنبيهات الأزمات",
    crisisAlertsHint: "شريط تنبيه وإشعار فوري عند وقوع أزمة في دولة تتابعها",
    reminders: "ذكّرني قبل",
    remindersHint: "أيام قبل كل مناسبة",
    daysBefore: "{{n}} ي",
    sameDay: "في اليوم نفسه",
    quietHours: "ساعات الهدوء",
    quietHoursHint: "تصل التذكيرات بصمت إلى قائمة الإشعارات (تنبيهات الأزمات تبقى فورية)",
    from: "من",
    to: "إلى",
    preview: "معاينة",
    previewHint: "ما ستراه بهذه الإعدادات",
    days90: "90 يوماً",
    days365: "سنة",
    previewTotal: "{{n}} مناسبة",
    previewEmpty: "لا شيء مطابق بعد. أضف دولة أو طابعاً.",
    previewMore: "+{{n}} أخرى",
    save: "حفظ الاشتراك",
    saved: "تم حفظ اشتراك التقويم",
    saveFailed: "تعذّر حفظ الاشتراك",
    needCountry: "اختر دولة واحدة على الأقل أو «حول العالم»",
  },
  crisis: {
    title: "تنبيه أزمة",
    body: "{{title}}. يُنصح بإيقاف المنشورات الترويجية مؤقتاً.",
    more: "و{{n}} غيرها",
    view: "عرض",
    hide: "إخفاء التنبيه",
  },
  conflict: {
    critical: "يصادف {{event}}: أوقف هذا المنشور",
    warning: "يصادف {{event}}: يُفضّل إعادة الجدولة",
    review_tone: "يصادف {{event}}: راجع النبرة",
    moveTo: "انقل إلى {{when}}",
    moved: "نُقل إلى {{when}}",
    pause: "إيقاف (إرجاع إلى المسودات)",
    paused: "أُوقف وأُعيد إلى المسودات",
    badge: "تعارض مع التقويم",
  },
  composer: {
    for: "تكتب بمناسبة {{event}}",
    drafting: "نكتب مسودة تعليق لهذا اليوم…",
    drafted: "مسودة الذكاء الاصطناعي جاهزة بالنبرة المناسبة",
    avoid: "تجنّب",
  },
  settings: {
    title: "التقويم",
    description: "الأيام الوطنية والعطل والأزمات في تقويم المحتوى.",
    notifications: {
      title: "إشعارات التقويم",
      hint: "لك وحدك. تصل داخل التطبيق (قائمة الإشعارات والتنبيهات الفورية).",
      reminders: "المناسبات القادمة",
      remindersHint: "قبل الأيام التي تتابعها، مع إرشادات النشر",
      crisis: "تنبيهات الأزمات",
      crisisHint: "فوراً وبأولوية عالية",
      conflicts: "تعارض المنشورات",
      conflictsHint: "عندما يصادف منشور مجدول يوم حزن أو أزمة",
      digest: "الملخص",
      digestOff: "متوقف",
      digestDaily: "يومي",
      digestWeekly: "أسبوعي",
      saved: "تم حفظ تفضيلات الإشعارات",
    },
    health: {
      title: "المصادر",
      hint: "تُفحص كل المصادر مرة يوميًا في الصباح الباكر. تعطّل مصدر لا يوقف البقية.",
      source: "المصدر",
      status: "الحالة",
      lastSuccess: "آخر نجاح",
      details: "تفاصيل",
      never: "أبداً",
      refresh: "تحديث الآن",
      refreshing: "جارٍ تحديث التقويم…",
      refreshed: "تم تحديث التقويم",
      runs: "آخر التشغيلات",
      runLine: "+{{added}} جديدة · {{changed}} معدّلة · {{removed}} محذوفة",
      statuses: { ok: "يعمل", failing: "متعطّل", disabled: "متوقف", never_ran: "لم يعمل بعد" },
    },
  },
  toast: { open: "فتح" },
};
